import { useState } from "react";
import { Link } from "react-router-dom";
import { computeVPD } from "../utility/vpd.js";
import GrowPhaseBar from "./GrowPhaseBar.jsx";
import StabilityScore from "./StabilityScore.jsx";
import { useUpdateGrow, useDeleteGrow, useStabilityScore } from "../hooks/useGrowsQuery.js";
import Card from "./ui/Card.jsx";
import Badge from "./ui/Badge.jsx";
import Input from "./ui/Input.jsx";
import Button from "./ui/Button.jsx";

function vpdColor(vpd) {
  if (vpd == null) return "text-gray-400";
  if (vpd < 0.1) return "text-blue-700";
  if (vpd < 0.5) return "text-green-600";
  if (vpd < 0.9) return "text-yellow-500";
  return "text-red-600";
}

function vpdDot(vpd) {
  if (vpd == null) return "bg-gray-300";
  if (vpd < 0.1) return "bg-blue-500";
  if (vpd < 0.5) return "bg-green-500";
  if (vpd < 0.9) return "bg-yellow-400";
  return "bg-red-500";
}

const STATUS_BADGE_COLOR = {
  active:    "success",
  completed: "gray",
  abandoned: "danger",
};

export default function GrowCard({ grow, liveDevice, hostname }) {
  const currentPhase = grow.current_phase;
  const deviceLabel = grow.device_display_name ?? grow.device_hostname ?? "No device";

  const updateGrow = useUpdateGrow(grow.id);
  const deleteGrow = useDeleteGrow();
  const stabilityScore = useStabilityScore({ hostname, currentPhase });

  const [renaming, setRenaming] = useState(false);
  const [nameInput, setNameInput] = useState(grow.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  let tempF = null;
  let rh = null;
  if (liveDevice) {
    for (const sensor of liveDevice.sensors ?? []) {
      for (const metric of sensor.metrics ?? []) {
        if (metric.metric === "temperature" && metric.latest_value != null) tempF = metric.latest_value;
        if (metric.metric === "humidity" && metric.latest_value != null) rh = metric.latest_value;
      }
    }
  }
  const vpd = tempF != null && rh != null ? computeVPD(tempF, rh) : null;

  async function handleRename(e) {
    e.preventDefault();
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === grow.name) { setRenaming(false); return; }
    setError(null);
    try {
      await updateGrow.mutateAsync({ name: trimmed, status: grow.status });
      setRenaming(false);
    } catch {
      setError("Failed to rename");
    }
  }

  function toggleFavorite(e) {
    e.preventDefault();
    e.stopPropagation();
    updateGrow.mutate({ is_favorite: !grow.is_favorite });
  }

  async function handleDelete() {
    setError(null);
    try {
      await deleteGrow.mutateAsync(grow.id);
    } catch {
      setError("Failed to delete");
    }
  }

  return (
    <Card className="p-4 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {renaming ? (
            <form onSubmit={handleRename} className="flex items-center gap-2">
              <Input
                autoFocus
                size="sm"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="text-sm font-semibold border-sky-400 py-0.5 w-full"
              />
              <Button
                type="submit"
                size="sm"
                disabled={updateGrow.isPending}
                className="shrink-0 px-2 py-0.5"
              >
                Save
              </Button>
              <button
                type="button"
                onClick={() => { setRenaming(false); setNameInput(grow.name); }}
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
              >
                Cancel
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={toggleFavorite}
                aria-label={grow.is_favorite ? "Unfavorite grow" : "Favorite grow"}
                aria-pressed={grow.is_favorite}
                title={grow.is_favorite ? "Remove from dashboard favorites" : "Pin to dashboard favorites"}
                className={`shrink-0 -ml-0.5 inline-flex items-center gap-1 leading-none transition-colors ${
                  grow.is_favorite ? "text-amber-500 hover:text-amber-600" : "text-gray-400 dark:text-gray-500 hover:text-amber-500"
                }`}
              >
                <span className="text-lg leading-none">{grow.is_favorite ? "★" : "☆"}</span>
                <span className="text-xs font-medium">Favorite</span>
              </button>
              <h3 className="text-sky-800 dark:text-sky-300 font-semibold text-base">{grow.name}</h3>
              {grow.species && (
                <span className="text-xs bg-sky-50 dark:bg-sky-950 text-sky-600 dark:text-sky-400 border border-sky-200 dark:border-sky-800 rounded-full px-2 py-0.5">
                  {grow.species}
                </span>
              )}
              <Badge color={STATUS_BADGE_COLOR[grow.status] ?? STATUS_BADGE_COLOR.active}>
                {grow.status}
              </Badge>
            </div>
          )}
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{deviceLabel}</div>
        </div>
        {!renaming && (
          <div className="flex items-start gap-3 shrink-0">
            {vpd != null && (
              <div className="flex flex-col items-end">
                <div className={`flex items-center gap-1.5 text-sm font-bold tabular-nums ${vpdColor(vpd)}`}>
                  <span className={`w-2 h-2 rounded-full ${vpdDot(vpd)}`} />
                  {vpd.toFixed(2)} kPa
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500">VPD</div>
              </div>
            )}
            <StabilityScore score={stabilityScore} size="sm" />
          </div>
        )}
      </div>

      {/* Phase bar */}
      <GrowPhaseBar phases={grow.all_phases ?? []} />

      {/* Live readings */}
      {liveDevice && (
        <div className="flex gap-4 text-sm">
          {tempF != null && (
            <div className="text-center">
              <div className="font-bold tabular-nums text-gray-700 dark:text-gray-200">{tempF.toFixed(1)}<span className="text-xs font-normal">°F</span></div>
              <div className="text-xs text-gray-400 dark:text-gray-500">Temp</div>
            </div>
          )}
          {rh != null && (
            <div className="text-center">
              <div className="font-bold tabular-nums text-gray-700 dark:text-gray-200">{rh.toFixed(0)}<span className="text-xs font-normal">%</span></div>
              <div className="text-xs text-gray-400 dark:text-gray-500">RH</div>
            </div>
          )}
        </div>
      )}

      {/* Stats row */}
      <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-50 dark:border-gray-700 pt-2">
        <span className="text-success dark:text-success-onDark"><span className="font-semibold">{grow.total_flushes ?? 0}</span> flush{grow.total_flushes !== 1 ? "es" : ""}</span>
        {grow.total_weight_grams > 0 && <span className="text-success dark:text-success-onDark"><span className="font-semibold">{grow.total_weight_grams}g</span> wet</span>}
        {grow.total_dry_weight_grams > 0 && <span className="text-success dark:text-success-onDark"><span className="font-semibold">{grow.total_dry_weight_grams}g</span> dry</span>}
        <span>{grow.note_count ?? 0} note{grow.note_count !== 1 ? "s" : ""}</span>
      </div>

      {error && <div className="text-xs text-red-600">{error}</div>}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
          <p className="text-red-700 dark:text-red-400 font-medium mb-1">Delete "{grow.name}"?</p>
          <p className="text-red-600 dark:text-red-400 text-xs mb-3">This cannot be undone. All phases, notes, and harvest records will be permanently deleted.</p>
          <div className="flex gap-2">
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              disabled={deleteGrow.isPending}
            >
              {deleteGrow.isPending ? "Deleting…" : "Yes, delete"}
            </Button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      {!confirmDelete && (
        <div className="flex gap-2 mt-1">
          <Link
            to={`/grows/${grow.id}`}
            className="text-xs text-sky-700 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 border border-sky-200 dark:border-sky-800 rounded px-2 py-1 hover:bg-sky-50 dark:hover:bg-sky-950 transition-colors"
          >
            View Details
          </Link>
          <button
            onClick={() => { setRenaming(true); setNameInput(grow.name); }}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Rename
          </button>
          <button
            onClick={() => setConfirmDelete(true)}
            className="text-xs text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 border border-red-100 dark:border-red-900 rounded px-2 py-1 hover:bg-red-50 dark:hover:bg-red-950 transition-colors ml-auto"
          >
            Delete
          </button>
        </div>
      )}
    </Card>
  );
}
